import type { ReportOutputFileDto, ReportOutputRequestDto } from '@supply-mover-2-0/shared';

/** How soon the Downloads panel first asks after the files it is waiting for. */
export const BACKGROUND_DOWNLOAD_POLL_MS = 2_000;

/** The longest it waits between checks once a file has been building a while. */
export const BACKGROUND_DOWNLOAD_MAX_POLL_MS = 30_000;

/**
 * The wait before the next check: 2 seconds at first, half again longer each
 * time, up to 30 seconds, so a quick file arrives quickly and a long build
 * is not asked about every 2 seconds.
 */
export function backgroundPollDelay(checks: number): number {
	return Math.min(BACKGROUND_DOWNLOAD_MAX_POLL_MS, Math.round(BACKGROUND_DOWNLOAD_POLL_MS * 1.5 ** Math.max(0, checks)));
}

/**
 * A file picked up again after a reload is followed only if it was asked for
 * this recently; an older one still building is left to the bell.
 */
export const BACKGROUND_DOWNLOAD_RESUME_WINDOW_MS = 60 * 60 * 1000;

/**
 * A file that became ready while the page was reloading (or closed) is still
 * downloaded on load if it finished this recently and nobody downloaded it.
 */
export const BACKGROUND_DOWNLOAD_READY_RESUME_WINDOW_MS = 10 * 60 * 1000;

/** One row in the Downloads panel. */
export interface BackgroundDownloadModel {
	file: ReportOutputFileDto;
	/** What was asked for, so a failed file can be asked for again. Unknown after a reload. */
	request: ReportOutputRequestDto | null;
	/** The browser has been handed the file. */
	delivered: boolean;
	/** Handing it over failed; the row offers Download instead of trying again on its own. */
	deliveryFailed: boolean;
	/**
	 * Still building an hour after it was asked for: the panel stopped asking
	 * and shows it as not finished (the bell still brings it if it finishes).
	 */
	notFinished?: boolean;
	/**
	 * PD-17520 — it became ready while the panel was not checking (a frozen
	 * hidden tab, a sleeping laptop) for longer than the bell waits, so the bell
	 * may have rung: the row offers Download instead of saving it on its own,
	 * so nobody gets both.
	 */
	leftToBell?: boolean;
}

/** Still being built by the worker. */
export function isActiveDownload(file: Pick<ReportOutputFileDto, 'status'>): boolean {
	return file.status === 'queued' || file.status === 'running';
}

/** Still being built and still followed by the panel. */
export function isFollowedDownload(item: Pick<BackgroundDownloadModel, 'file' | 'notFinished'>): boolean {
	return isActiveDownload(item.file) && item.notFinished !== true;
}

/** Asked for longer ago than the panel follows a file (the resume window). */
export function isPastFollowWindow(file: Pick<ReportOutputFileDto, 'createdAt'>, now: number): boolean {
	const created = Date.parse(file.createdAt);
	return Number.isFinite(created) && now - created > BACKGROUND_DOWNLOAD_RESUME_WINDOW_MS;
}

/** The HTTP status of a failed API call, when it carries one. */
export function apiErrorStatus(error: unknown): number | null {
	if (typeof error !== 'object' || error == null || !('apiError' in error)) {
		return null;
	}
	const apiError: unknown = error.apiError;
	if (typeof apiError !== 'object' || apiError == null || !('status' in apiError)) {
		return null;
	}
	return typeof apiError.status === 'number' ? apiError.status : null;
}

/** Ready but not yet handed to the browser: the panel downloads these on its own. */
export function needsDelivery(item: BackgroundDownloadModel): boolean {
	return item.file.status === 'ready' && !item.delivered && !item.deliveryFailed && item.leftToBell !== true;
}

/** The panel's heading: "Preparing 2 downloads", "1 download ready", "2 downloads complete" or "Downloads". */
export function downloadsHeading(items: ReadonlyArray<BackgroundDownloadModel>): { text: string; count: number } {
	const active = items.filter(isFollowedDownload).length;
	if (active > 0) {
		return { text: active === 1 ? 'Preparing 1 download' : 'Preparing {{count}} downloads', count: active };
	}
	const ready = items.filter((item) => item.file.status === 'ready');
	if (ready.length > 0) {
		// Ready but not saved (the hand-off failed): the row offers Download.
		const waiting = ready.filter((item) => !item.delivered).length;
		if (waiting > 0) {
			return { text: waiting === 1 ? '1 download ready' : '{{count}} downloads ready', count: waiting };
		}
		return { text: ready.length === 1 ? '1 download complete' : '{{count}} downloads complete', count: ready.length };
	}
	return { text: 'Downloads', count: items.length };
}

/**
 * Files to pick up again after a reload: still building and asked for in the
 * last hour, or ready in the last few minutes and not yet downloaded.
 */
export function filesToResume(files: ReadonlyArray<ReportOutputFileDto>, now: number): Array<ReportOutputFileDto> {
	return files.filter((file) => {
		if (isActiveDownload(file)) {
			const created = Date.parse(file.createdAt);
			return Number.isFinite(created) && now - created <= BACKGROUND_DOWNLOAD_RESUME_WINDOW_MS;
		}
		if (file.status === 'ready' && !file.downloaded && file.readyAt != null) {
			const ready = Date.parse(file.readyAt);
			return Number.isFinite(ready) && now - ready <= BACKGROUND_DOWNLOAD_READY_RESUME_WINDOW_MS;
		}
		return false;
	});
}

/**
 * Hands a stored file's link to the browser. The link names the file and
 * marks it as an attachment, so the browser saves it to Downloads without
 * leaving the page or asking for a name.
 */
export function startBrowserDownload(url: string): void {
	const link = document.createElement('a');
	link.href = url;
	link.rel = 'noopener';
	link.style.display = 'none';
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
}
